import { pascalToSentenceCase } from "../utils";
import {
  getDefaultExcelFormatForDataType,
  type ColumnConfigRow,
  type FilterConfigRow,
  type TemplateWorkbookConfig,
} from "./TemplateWorkbookService";

export interface EntityTypeItem {
  key: string;
  displayName: string;
  description?: string;
  dataEndpoint: string;
}

export const formatEntityTypeLabel = (entity: Pick<EntityTypeItem, "key" | "displayName">): string => {
  const entityCode = entity.key.trim();
  const entityName = entity.displayName.trim();

  if (!entityCode) return entityName;
  if (!entityName) return entityCode;
  return `[${entityCode}] - ${entityName}`;
};

export interface ApiFilter {
  entityName?: string;
  entityCode?: string;
  filterColumnName?: string;
  idColumnName?: string;
  displayColumnName?: string;
  multiSelect?: boolean;
  type?: string;
  default?: string;
  required?: boolean;
  filters?: Array<string | ApiFilter>;
}

export interface ApiColumn {
  name: string;
  dataType: string;
  displayName?: string;
  included?: boolean;
  filter?: boolean;
}

export interface FilterDefinition {
  key: string;
  displayName: string;
  apiField: string;
  displayField: string | null;
  filterControlType: string;
  valueType: string;
  requiredSupported: boolean;
  supportsCascadeParent: boolean;
  cascadeParents: string[];
  defaultRequired: boolean;
  dropdownEndpoint: string;
  allowedTypes: string[];
  generatedFromColumn?: boolean;
  includeInDataCall: boolean;
}

export interface ColumnDefinition {
  key: string;
  displayName: string;
  apiField: string;
  dataType: string;
  defaultSelected: boolean;
}

export interface FilterRow {
  key: string;
  displayName: string;
  apiField: string;
  displayField: string | null;
  type: string;
  required: boolean;
  cascadeFrom: string | null;
  cascadeParents: string[];
  dropdownEndpoint: string;
  allowedTypes: string[];
  generatedFromColumn: boolean;
  includeInDataCall: boolean;
  filterValue: string;
}

export interface ColumnRow {
  key: string;
  displayName: string;
  apiField: string;
  dataType: string;
  excelFormat: string;
}

export const FILTER_TYPE_LABELS: Record<string, string> = {
  select: "Select",
  multiselect: "Multi-select",
  date: "Date",
  number: "Number",
  text: "Text",
};

const DATA_TYPE_LABELS: Record<string, string> = {
  text: "Text",
  date: "Date",
  number: "Number",
  string: "Text",
  int32: "Integer",
  int64: "Integer",
  int16: "Integer",
  single: "Number",
  double: "Number",
  decimal: "Number",
  datetime: "Date Time",
  datetimeoffset: "Date Time",
  time: "Time",
  timespan: "Duration",
  boolean: "Boolean",
  guid: "Text",
  byte: "Integer",
  sbyte: "Integer",
};

export const ALL_FILTER_TYPES = ["select", "multiselect", "date", "number", "text"];

export const mapColumnDataTypeToFriendlyLabel = (dataType: string): string => {
  const normalized = (dataType || "").toLowerCase();
  if (["string", "text", "guid", "boolean"].includes(normalized)) return "Text";
  if (["int16", "int32", "int64", "byte", "sbyte"].includes(normalized)) return "Integer";
  if (["single", "double", "decimal", "number"].includes(normalized)) return "Number";
  return DATA_TYPE_LABELS[normalized] ?? dataType;
};

const normalizeLookupToken = (value: string): string => value.trim().toLowerCase();

const inferFilterControlType = (apiFilter: ApiFilter): string => {
  const normalizedType = (apiFilter.type || "").toLowerCase().replace(/[\s_-]+/g, "");
  const hasDropdownFields = Boolean(apiFilter.idColumnName || apiFilter.displayColumnName);

  if (["date", "datetime", "datetimeoffset", "time"].includes(normalizedType)) return "date";
  if (["number", "int16", "int32", "int64", "single", "double", "decimal", "byte", "sbyte"].includes(normalizedType)) return "number";
  if (hasDropdownFields) return apiFilter.multiSelect === false ? "select" : "multiselect";
  if (normalizedType === "select" || normalizedType === "multiselect") return normalizedType;
  if (apiFilter.multiSelect === true) return "multiselect";
  if (apiFilter.multiSelect === false) return "select";
  return "text";
};

const inferFilterTypeFromColumnDataType = (dataType: string): string => {
  const normalized = (dataType || "").toLowerCase();
  if (["date", "datetime", "datetimeoffset", "time", "timespan"].includes(normalized)) return "date";
  if (["number", "int16", "int32", "int64", "single", "double", "decimal", "byte", "sbyte"].includes(normalized)) return "number";
  return "text";
};

export const mergeApiAndColumnFilters = (
  apiFilters: FilterDefinition[],
  columnFilters: FilterDefinition[]
): FilterDefinition[] => {
  const merged = [...apiFilters];
  const usedTokens = new Set<string>();

  const addToken = (value: string | undefined) => {
    if (!value) return;
    const normalized = normalizeLookupToken(value);
    if (normalized) usedTokens.add(normalized);
  };

  apiFilters.forEach((filter) => {
    addToken(filter.key);
    addToken(filter.apiField);
    addToken(filter.displayName);
  });

  columnFilters.forEach((columnFilter) => {
    const tokens = [columnFilter.key, columnFilter.apiField, columnFilter.displayName]
      .map((value) => normalizeLookupToken(value))
      .filter(Boolean);
    const exists = tokens.some((token) => usedTokens.has(token));
    if (exists) return;
    merged.push(columnFilter);
    tokens.forEach((token) => usedTokens.add(token));
  });

  return merged;
};

const mapApiFilterToDefinition = (
  apiFilter: ApiFilter,
  selectedEntityType: string
): FilterDefinition | null => {
  const key = apiFilter.filterColumnName || apiFilter.idColumnName || apiFilter.displayColumnName || "";
  if (!key) return null;

  const filterControlType = inferFilterControlType(apiFilter);
  const displayField = apiFilter.displayColumnName && apiFilter.displayColumnName !== apiFilter.idColumnName
    ? apiFilter.displayColumnName
    : null;
  const apiField = apiFilter.idColumnName || apiFilter.filterColumnName || key;
  const dropdownEndpoint = filterControlType === "select" || filterControlType === "multiselect"
    ? (apiFilter.entityCode || selectedEntityType)
    : "";

  return {
    key,
    displayName: pascalToSentenceCase(apiFilter.filterColumnName || apiFilter.displayColumnName || key),
    apiField,
    displayField,
    filterControlType,
    valueType: filterControlType,
    requiredSupported: true,
    supportsCascadeParent: false,
    cascadeParents: [],
    defaultRequired: Boolean(apiFilter.required),
    dropdownEndpoint,
    allowedTypes: filterControlType === "select" || filterControlType === "multiselect"
      ? ["select", "multiselect"]
      : ALL_FILTER_TYPES.includes(filterControlType)
        ? [filterControlType]
        : ["text"],
    includeInDataCall: true,
  };
};

const getApiFilterAliases = (apiFilter: ApiFilter, definition: FilterDefinition): string[] => {
  return [
    definition.key,
    definition.apiField,
    definition.displayName,
    apiFilter.entityName,
    apiFilter.entityCode,
    apiFilter.filterColumnName,
    apiFilter.idColumnName,
    apiFilter.displayColumnName,
  ].filter((value): value is string => Boolean(value));
};

const sortFiltersByDependencies = (filters: FilterDefinition[]): FilterDefinition[] => {
  const keyToFilter = new Map(filters.map((filter) => [filter.key, filter]));
  const childrenByParent = new Map<string, string[]>();
  const indegree = new Map<string, number>();
  const originalIndex = new Map<string, number>();

  filters.forEach((filter, index) => {
    indegree.set(filter.key, 0);
    originalIndex.set(filter.key, index);
  });

  filters.forEach((filter) => {
    filter.cascadeParents.forEach((parentKey) => {
      if (!keyToFilter.has(parentKey) || parentKey === filter.key) return;
      indegree.set(filter.key, (indegree.get(filter.key) || 0) + 1);
      const children = childrenByParent.get(parentKey) || [];
      children.push(filter.key);
      childrenByParent.set(parentKey, children);
    });
  });

  const queue = filters
    .map((filter) => filter.key)
    .filter((key) => (indegree.get(key) || 0) === 0)
    .sort((a, b) => (originalIndex.get(a) || 0) - (originalIndex.get(b) || 0));

  const sortedKeys: string[] = [];
  while (queue.length > 0) {
    const key = queue.shift() as string;
    sortedKeys.push(key);

    const children = (childrenByParent.get(key) || [])
      .slice()
      .sort((a, b) => (originalIndex.get(a) || 0) - (originalIndex.get(b) || 0));

    children.forEach((childKey) => {
      const nextIndegree = (indegree.get(childKey) || 0) - 1;
      indegree.set(childKey, nextIndegree);
      if (nextIndegree === 0) queue.push(childKey);
    });
  }

  if (sortedKeys.length < filters.length) {
    const included = new Set(sortedKeys);
    filters.forEach((filter) => {
      if (!included.has(filter.key)) sortedKeys.push(filter.key);
    });
  }

  return sortedKeys
    .map((key) => keyToFilter.get(key))
    .filter((filter): filter is FilterDefinition => Boolean(filter));
};

export const normalizeApiFilters = (
  apiFilters: ApiFilter[],
  selectedEntityType: string
): FilterDefinition[] => {
  const flattened: { source: ApiFilter; definition: FilterDefinition; dependencyTokens: string[] }[] = [];

  const walk = (filter: ApiFilter): string[] => {
    const definition = mapApiFilterToDefinition(filter, selectedEntityType);

    const dependencyTokens: string[] = [];
    (filter.filters || []).forEach((nested) => {
      if (typeof nested === "string") return;
      const nestedAliases = walk(nested);
      dependencyTokens.push(...nestedAliases);
    });

    if (definition) {
      flattened.push({
        source: filter,
        definition,
        dependencyTokens,
      });
    }

    return definition
      ? getApiFilterAliases(filter, definition)
      : [
          filter.filterColumnName,
          filter.idColumnName,
          filter.displayColumnName,
          filter.entityCode,
          filter.entityName,
        ].filter((value): value is string => Boolean(value));
  };

  apiFilters.forEach((filter) => {
    walk(filter);
  });

  const aliasToKey = new Map<string, string>();
  const keyToDefinition = new Map<string, FilterDefinition>();
  const dependencyTokensByKey = new Map<string, string[]>();
  const orderedKeys: string[] = [];

  const registerAlias = (alias: string | undefined, canonicalKey: string) => {
    if (!alias) return;
    const normalizedAlias = normalizeLookupToken(alias);
    if (!normalizedAlias || aliasToKey.has(normalizedAlias)) return;
    aliasToKey.set(normalizedAlias, canonicalKey);
  };

  const resolveExistingKey = (aliases: string[]): string | null => {
    for (const alias of aliases) {
      const normalizedAlias = normalizeLookupToken(alias);
      const existing = aliasToKey.get(normalizedAlias);
      if (existing) return existing;
    }
    return null;
  };

  flattened.forEach(({ source, definition, dependencyTokens }) => {
    const aliases = getApiFilterAliases(source, definition);
    const existingKey = resolveExistingKey(aliases);
    const canonicalKey = existingKey || definition.key;

    if (!keyToDefinition.has(canonicalKey)) {
      keyToDefinition.set(canonicalKey, { ...definition, key: canonicalKey, generatedFromColumn: false });
      orderedKeys.push(canonicalKey);
    } else {
      const existingDefinition = keyToDefinition.get(canonicalKey) as FilterDefinition;
      existingDefinition.defaultRequired = existingDefinition.defaultRequired || Boolean(definition.defaultRequired);
      existingDefinition.supportsCascadeParent =
        existingDefinition.supportsCascadeParent || Boolean(definition.supportsCascadeParent);
    }

    aliases.forEach((alias) => registerAlias(alias, canonicalKey));

    const existingDependencyTokens = dependencyTokensByKey.get(canonicalKey) || [];
    dependencyTokensByKey.set(canonicalKey, [...existingDependencyTokens, ...dependencyTokens]);
  });

  const resolvedWithoutInclude: FilterDefinition[] = [];
  orderedKeys.forEach((key) => {
    const definition = keyToDefinition.get(key);
    if (!definition) return;

    const dependencyTokens = dependencyTokensByKey.get(key) || [];
    const cascadeParents = Array.from(
      new Set(
        dependencyTokens
          .map((token) => aliasToKey.get(normalizeLookupToken(token)) || "")
          .filter((parentKey) => parentKey.length > 0 && parentKey !== key)
      )
    );

    resolvedWithoutInclude.push({
      ...definition,
      cascadeParents,
      supportsCascadeParent: cascadeParents.length > 0,
      generatedFromColumn: false,
      includeInDataCall: false,
    });
  });

  const filtersUsedAsDependencies = new Set<string>();
  resolvedWithoutInclude.forEach((filter) => {
    filter.cascadeParents.forEach((parentKey) => filtersUsedAsDependencies.add(parentKey));
  });

  const resolved = resolvedWithoutInclude.map((filter) => ({
    ...filter,
    includeInDataCall: !filtersUsedAsDependencies.has(filter.key),
  }));

  return sortFiltersByDependencies(resolved);
};

export const mapFilterDefinitionToRow = (def: FilterDefinition): FilterRow => ({
  key: def.key,
  displayName: def.displayName,
  apiField: def.apiField,
  displayField: def.displayField,
  type: def.filterControlType,
  required: def.defaultRequired,
  cascadeFrom: def.cascadeParents.length > 0 ? def.cascadeParents[0] : null,
  cascadeParents: def.cascadeParents,
  dropdownEndpoint: def.dropdownEndpoint,
  allowedTypes: def.allowedTypes,
  generatedFromColumn: Boolean(def.generatedFromColumn),
  includeInDataCall: def.includeInDataCall,
  filterValue: "",
});

export const getColumnGeneratedFilters = (columns: ApiColumn[]): FilterDefinition[] => {
  return columns
    .filter((column) => column.filter === true)
    .map((column) => {
      const filterType = inferFilterTypeFromColumnDataType(column.dataType);
      const key = column.name;
      return {
        key,
        displayName: column.displayName || pascalToSentenceCase(column.name),
        apiField: column.name,
        displayField: null,
        filterControlType: filterType,
        valueType: column.dataType || filterType,
        requiredSupported: true,
        supportsCascadeParent: false,
        cascadeParents: [],
        defaultRequired: false,
        dropdownEndpoint: "",
        allowedTypes: [filterType],
        generatedFromColumn: true,
        includeInDataCall: true,
      } as FilterDefinition;
    });
};

export const toColumnDefinition = (item: ApiColumn): ColumnDefinition => ({
  key: item.name,
  displayName: item.displayName || pascalToSentenceCase(item.name),
  apiField: item.name,
  dataType: item.dataType,
  defaultSelected: item.included ?? true,
});

export interface BuildTemplateWorkbookConfigInput {
  templateName: string;
  selectedEntityType: string;
  entityNames: EntityTypeItem[];
  selectedFilters: FilterRow[];
  selectedColumns: ColumnRow[];
  includeUdfs: boolean;
  selectedUdfs: string;
  hasUdfs: boolean;
  dataReferenceMode?: "none" | "standardTable";
}

export const buildTemplateWorkbookConfig = (
  input: BuildTemplateWorkbookConfigInput
): TemplateWorkbookConfig => {
  const entityDef = input.entityNames.find((e) => e.key === input.selectedEntityType);

  return {
    templateName: input.templateName,
    entityType: input.selectedEntityType,
    entityDataEndpoint: entityDef?.dataEndpoint ?? input.selectedEntityType.toLowerCase(),
    filters: input.selectedFilters.map((f): FilterConfigRow => ({
      key: f.key,
      displayName: f.displayName,
      apiField: f.apiField,
      displayField: f.displayField,
      type: f.type,
      required: f.required,
      cascadeFrom: f.cascadeFrom,
      dropdownEndpoint: f.dropdownEndpoint,
      fixedValue: f.generatedFromColumn ? f.filterValue.trim() : "",
      generatedFromColumn: f.generatedFromColumn,
      includeInDataCall: f.includeInDataCall,
    })),
    columns: input.selectedColumns.map((c): ColumnConfigRow => ({
      key: c.key,
      displayName: c.displayName,
      apiField: c.apiField,
      dataType: c.dataType,
      excelFormat: c.excelFormat,
    })),
    hasUdfs: input.hasUdfs,
    includeUdfs: input.includeUdfs,
    preselectedUdfs: input.includeUdfs ? input.selectedUdfs : "",
    dataReferenceMode: input.dataReferenceMode,
  };
};

export const toColumnRowsWithDefaultFormats = (columns: ColumnDefinition[]): ColumnRow[] => {
  return columns.map((c) => ({
    key: c.key,
    displayName: c.displayName,
    apiField: c.apiField,
    dataType: c.dataType,
    excelFormat: getDefaultExcelFormatForDataType(c.dataType),
  }));
};
